/**
 * Synchronous traffic acquisition capture.
 *
 * Captures source / medium / campaign / term / referrer on first paint and
 * persists in sessionStorage as `lt_acquisition`. Subsequent calls return the
 * cached value, so the original landing acquisition survives SPA navigation,
 * form fills, and any deferred analytics hooks.
 */

const ACQUISITION_KEY = 'lt_acquisition';
const SESSION_ID_KEY = 'visitor_session_id';
const VISITOR_ID_KEY = 'lt_visitor_id';
const FIRST_VISIT_KEY = 'lt_first_visit';

export interface Acquisition {
  source: string;
  medium: string | null;
  campaign: string | null;
  term: string | null;
  content: string | null;
  referrer_domain: string | null;
  entry_page: string;
  captured_at: string;
}

const getReferrerDomain = (): string | null => {
  if (!document.referrer) return null;
  try {
    const url = new URL(document.referrer);
    const host = url.hostname.replace(/^www\./, '');
    if (host.includes('lovable.dev') || host.includes('lovableproject.com')) return null;
    if (host === window.location.hostname) return null;
    return host;
  } catch {
    return null;
  }
};

const detectSource = (params: URLSearchParams): string => {
  const utmSource = params.get('utm_source');
  if (params.get('gclid') || params.get('gad_source')) return 'google_ads';
  if (utmSource === 'sms' || params.get('utm_medium') === 'sms') return 'sms';
  if (utmSource === 'email' || utmSource === 'signature') return 'email';
  if (utmSource) return utmSource;
  if (params.get('fbclid')) return 'facebook';

  const ref = document.referrer || '';
  if (ref.includes('google')) return 'google_organic';
  if (ref.includes('facebook') || ref.includes('instagram')) return 'facebook';
  if (ref.includes('bing')) return 'bing';
  if (ref.includes('brave')) return 'brave_search';
  if (ref.includes('reddit')) return 'reddit';
  if (ref.includes('duckduckgo')) return 'duckduckgo';
  if (ref.includes('yahoo')) return 'yahoo';
  if (ref.includes('lovable.dev') || ref.includes('lovableproject.com')) return 'direct';
  if (ref) {
    try {
      const refHost = new URL(ref).hostname;
      if (refHost && !refHost.includes(window.location.hostname)) return 'referral';
    } catch {
      /* noop */
    }
  }
  return 'direct';
};

/**
 * Ensures session_id and visitor_id exist synchronously. Safe to call on every
 * page render — only writes once.
 */
export const ensureIdentifiers = (): { sessionId: string; visitorId: string; isReturning: boolean } => {
  let sessionId = sessionStorage.getItem(SESSION_ID_KEY);
  if (!sessionId) {
    sessionId = `visitor_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    sessionStorage.setItem(SESSION_ID_KEY, sessionId);
    sessionStorage.setItem('analytics_session_id', sessionId);
  }

  let visitorId = localStorage.getItem(VISITOR_ID_KEY);
  const hadVisitor = !!visitorId;
  if (!visitorId) {
    visitorId = `v_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    localStorage.setItem(VISITOR_ID_KEY, visitorId);
    localStorage.setItem(FIRST_VISIT_KEY, Date.now().toString());
  }

  return { sessionId, visitorId, isReturning: hadVisitor };
};

/**
 * Captures acquisition data synchronously on first call. Returns cached value
 * on subsequent calls so the original landing attribution is never overwritten.
 */
export const captureAcquisition = (): Acquisition => {
  ensureIdentifiers();

  const cached = sessionStorage.getItem(ACQUISITION_KEY);
  if (cached) {
    try {
      return JSON.parse(cached) as Acquisition;
    } catch {
      /* fall through and recapture */
    }
  }

  const params = new URLSearchParams(window.location.search);
  const acquisition: Acquisition = {
    source: detectSource(params),
    medium: params.get('utm_medium') || (params.get('gclid') ? 'cpc' : null),
    campaign: params.get('utm_campaign'),
    term: params.get('utm_term'),
    content: params.get('utm_content'),
    referrer_domain: getReferrerDomain(),
    entry_page: window.location.pathname,
    captured_at: new Date().toISOString(),
  };

  try {
    sessionStorage.setItem(ACQUISITION_KEY, JSON.stringify(acquisition));
  } catch {
    /* sessionStorage might be unavailable in some embeds — non-fatal */
  }

  return acquisition;
};

/** Read cached acquisition without writing. Returns null if not yet captured. */
export const getAcquisition = (): Acquisition | null => {
  const cached = sessionStorage.getItem(ACQUISITION_KEY);
  if (!cached) return null;
  try {
    return JSON.parse(cached) as Acquisition;
  } catch {
    return null;
  }
};
